"use client";

import { MessageInput } from "./message-input";
import { MessageSuggestions } from "./message-suggestions";
import { ThreadContent } from "./thread-content";
import { ThreadDropdown } from "./thread-dropdown";
import { ScrollableMessageContainer } from "./scrollable-message-container";
import { cn } from "../lib/utils";
import { useCartAssistant } from "../lib/cartAssistant/CartAssistantProvider";
import {
  MessageSquare,
  X,
  ChevronRight,
  Maximize2,
  Minimize2,
  Sparkles,
  ShoppingBag,
  AlertTriangle,
  Plus
} from "lucide-react";
import * as React from "react";
import { motion, useMotionValue, useDragControls } from "framer-motion";
import { createPortal } from "react-dom";
import { resolveAssistantPosition, saveAssistantPosition } from "../lib/assistantPosition";
import { useUIStore } from "../lib/ui-store";

import { SupabaseClient } from "@supabase/supabase-js";
import { useTranslation } from 'react-i18next';
import { useStore } from "../lib/store";

/**
 * Collapsible Message Thread component
 *
 * This component provides a floating chat widget that can be expanded and collapsed.
 */

export interface MessageThreadCollapsibleProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onPreviewImage: (url: string | null) => void;
  supabaseClient: SupabaseClient | null;
  userId: string | undefined;
}

export function MessageThreadCollapsible({
  open: openProp,
  onOpenChange,
  onPreviewImage,
  supabaseClient,
  userId
}: MessageThreadCollapsibleProps) {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { isStreaming, isWaiting, messages, startNewThread } = useCartAssistant();
  const isBusy = isStreaming || isWaiting;

  const THREAD_LIMIT = 50;
  const messageCount = messages?.length ?? 0;
  const isNearLimit = messageCount >= 45 && messageCount < THREAD_LIMIT;
  const isAtLimit = messageCount >= THREAD_LIMIT;
  const chatPosition = useStore(state => state.chatPosition);
  const setChatPosition = useStore(state => state.setChatPosition);
  const isMenuOpen = useUIStore(state => state.isMenuOpen);
  const [viewport, setViewport] = React.useState(() => ({
    width: typeof window === 'undefined' ? 1000 : window.innerWidth,
    height: typeof window === 'undefined' ? 1000 : window.innerHeight,
  }));
  const position = resolveAssistantPosition(chatPosition, viewport);
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const dragging = React.useRef(false);
  const dragControls = useDragControls();
  const launcherX = useMotionValue(position.x);
  const launcherY = useMotionValue(position.y);

  // Update actual motion values even when a drop clamps to the same saved point.
  const finishDrag = React.useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    const newPos = saveAssistantPosition({ x: launcherX.get(), y: launcherY.get() }, {
      width: window.innerWidth, height: window.innerHeight,
    });
    launcherX.stop(); launcherY.stop();
    launcherX.set(newPos.x); launcherY.set(newPos.y);
    setChatPosition(newPos);
    if (supabaseClient && userId) useStore.getState().syncStateToCloud(supabaseClient, userId);
  }, [launcherX, launcherY, setChatPosition, supabaseClient, userId]);

  React.useEffect(() => {
    if (!dragging.current) {
      launcherX.set(position.x); launcherY.set(position.y);
    }
  }, [position.x, position.y, launcherX, launcherY]);

  React.useEffect(() => {
    const recoverDrag = () => { dragControls.cancel(); finishDrag(); };
    window.addEventListener('blur', recoverDrag);
    window.addEventListener('pointercancel', recoverDrag);
    return () => {
      window.removeEventListener('blur', recoverDrag);
      window.removeEventListener('pointercancel', recoverDrag);
    };
  }, [dragControls, finishDrag]);

  React.useEffect(() => {
    const handleResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isOpen = openProp !== undefined ? openProp : internalOpen;
  const hasDragged = React.useRef(false);

  // Reset expansion state whenever chat opens
  React.useEffect(() => {
    if (isOpen) {
      setIsExpanded(false);
    }
  }, [isOpen]);

  const toggleOpen = () => {
    const next = !isOpen;
    if (onOpenChange) {
      onOpenChange(next);
    } else {
      setInternalOpen(next);
    }
  };

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div ref={viewportRef} className="fixed inset-0 pointer-events-none z-[80]"
      aria-hidden={isMenuOpen || undefined} inert={isMenuOpen}
      style={{ visibility: isMenuOpen ? 'hidden' : undefined }}>
      {/* Chat window */}
      {isOpen && (
        <>
          <div 
            className={cn(
              "absolute bg-white flex flex-col pointer-events-auto overflow-hidden animate-in fade-in zoom-in-95 duration-300",
              isExpanded 
                ? "inset-0" 
                : "bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-[420px] h-[min(640px,calc(100dvh-8rem))] rounded-[2rem] shadow-[0_24px_64px_-16px_rgba(26,43,60,0.35)] border border-gray-100"
            )}
          >
            {/* Header */}
            <div className={cn(
              "px-4 py-3 border-b border-gray-100 bg-white flex items-center justify-between gap-3",
              isExpanded && "pt-6 sm:pt-8"
            )}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center overflow-hidden shrink-0 ring-1 ring-emerald-100">
                  <img src="/icon-96x96.png" alt="" className="w-8 h-8 object-contain" />
                </div>
                <div className="min-w-0">
                  <p className="text-[15px] font-black text-[#1a2b3c] leading-tight truncate">{t('chat.assistant')}</p>
                  <p className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 leading-tight mt-0.5">
                    <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", isBusy ? "bg-amber-400 animate-pulse" : "bg-emerald-500")} />
                    {isBusy ? t('chat.thinking') : t('chat.status_online')}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-0.5 shrink-0">
                <ThreadDropdown />
                <button 
                  onClick={toggleExpanded}
                  className="w-9 h-9 hover:bg-gray-100 rounded-xl transition-colors active:scale-95 flex items-center justify-center text-gray-400 hover:text-[#1a2b3c]"
                  title={isExpanded ? t('panel.minimize') : t('panel.expand')}
                  aria-label={isExpanded ? t('panel.minimize') : t('panel.expand')}
                >
                  {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <button 
                  onClick={toggleOpen}
                  className="w-9 h-9 hover:bg-rose-50 rounded-xl transition-colors active:scale-95 flex items-center justify-center text-gray-400 hover:text-rose-500"
                  title={t('auth.close')}
                  aria-label={t('auth.close')}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Messages area */}
            <ScrollableMessageContainer className="flex-1 bg-gradient-to-b from-white to-gray-50/60">
              <div className={cn("w-full h-full mx-auto", isExpanded && "max-w-2xl")}>
                <ThreadContent />
              </div>
            </ScrollableMessageContainer>

            {/* Thread length warning */}
            {(isNearLimit || isAtLimit) && (
              <div className={cn(
                "px-4 py-2.5 flex items-center justify-between gap-3 border-t",
                isAtLimit
                  ? "bg-rose-50 border-rose-100"
                  : "bg-amber-50 border-amber-100"
              )}>
                <div className="flex items-center gap-2 min-w-0">
                  <AlertTriangle size={13} className={isAtLimit ? "text-rose-400 shrink-0" : "text-amber-400 shrink-0"} />
                  <span className={cn("text-[10px] font-black uppercase tracking-wide truncate", isAtLimit ? "text-rose-500" : "text-amber-600")}>
                    {isAtLimit
                      ? t('chat.thread_full')
                      : t('chat.messages_left_before_save', { count: THREAD_LIMIT - messageCount })}
                  </span>
                </div>
                <button
                  onClick={() => startNewThread()}
                  className={cn(
                    "flex items-center gap-1 px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider shrink-0 transition-all active:scale-95",
                    isAtLimit
                      ? "bg-rose-500 text-white hover:bg-rose-600"
                      : "bg-amber-400 text-white hover:bg-amber-500"
                  )}
                >
                  <Plus size={10} strokeWidth={3} />
                  {t('chat.new_chat')}
                </button>
              </div>
            )}

            {/* Suggestions & Input */}
            <div className={cn("bg-white border-t border-gray-100 relative")}>
              {messageCount > 0 && !isNearLimit && !isAtLimit && (
                <span className="absolute -top-6 right-4 text-[10px] font-semibold text-gray-300 tabular-nums pointer-events-none">
                  {t('chat.message_count', { count: messageCount, limit: THREAD_LIMIT })}
                </span>
              )}
              <div className={cn("w-full mx-auto", isExpanded && "max-w-2xl")}>
                <MessageSuggestions />
                <MessageInput
                  onPreviewImage={onPreviewImage}
                  supabaseClient={supabaseClient}
                  userId={userId}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Draggable Floating button */}
      {!isOpen && (
        <motion.div
          drag
          dragMomentum={false}
          dragControls={dragControls}
          dragConstraints={viewportRef}
          dragElastic={0}
          initial={false}
          onDragStart={() => { dragging.current = true; hasDragged.current = true; }}
          onDrag={(_, info) => {
            if (Math.abs(info.offset.x) > 4 || Math.abs(info.offset.y) > 4) {
              hasDragged.current = true;
            }
          }}
          onDragEnd={finishDrag}
          className="absolute pointer-events-auto touch-none left-0 top-0 z-[101]"
          style={{ x: launcherX, y: launcherY, width: 56, height: 56 }}
        >
          <button title={t('settings.sidekick_default', { defaultValue: 'CartAssistant' })}
            aria-label={t('settings.sidekick_default', { defaultValue: 'CartAssistant' })}
            onClick={() => {
              if (hasDragged.current) { hasDragged.current = false; return; }
              toggleOpen();
            }}
            className={cn(
              "h-14 w-14 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95 bg-white text-emerald-600 relative group overflow-hidden border border-gray-100"
            )}
          >
            {/* Animated Background Pulse */}
            <div className="absolute inset-0 bg-emerald-50 animate-pulse group-hover:scale-150 transition-transform duration-500" />
            
            <div className="relative z-10 flex flex-col items-center pointer-events-none">
              <img src="/icon-96x96.png" alt="" draggable={false} className="w-9 h-9 select-none object-contain" />
              
              {isStreaming && (
                <span className="absolute -top-2 -right-2 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400"></span>
                </span>
              )}
            </div>
          </button>
        </motion.div>
      )}
    </div>, document.body
  );
}
